// CustomSliderComponent.js
import { Formio } from "formiojs";
import Field from "formiojs/components/_classes/field/Field";

// Slider backed by a native <input type="range"> - no external dependencies.
class CustomSliderComponent extends Field {
  static schema(...extend) {
    return Field.schema(
      {
        type: "slider",
        label: "Slider",
        key: "slider",
        min: 0,
        max: 100,
        step: 1,
        defaultValue: 0,
        showValue: true,
      },
      ...extend
    );
  }

  static get builderInfo() {
    return {
      title: "Slider",
      icon: "sliders",
      group: "basic",
      weight: 75,
      schema: CustomSliderComponent.schema(),
    };
  }

  constructor(component, options, data) {
    super(component, options, data);
    // Whether the slider holds a real answer rather than just sitting at its
    // untouched default. Drives required-validation and whether Formio may
    // persist the default - see setValue()/shouldAddDefaultValue.
    this.touched = this.hasValue();
  }

  get defaultSchema() {
    return CustomSliderComponent.schema();
  }

  get inputInfo() {
    const info = super.inputInfo;
    info.type = "input";
    info.changeEvent = "input";
    return info;
  }

  toNumber(value, fallback) {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
  }

  get min() {
    return this.toNumber(this.component.min, 0);
  }

  get max() {
    return this.toNumber(this.component.max, 100);
  }

  get step() {
    const step = this.toNumber(this.component.step, 1);
    return step > 0 ? step : 1;
  }

  get emptyValue() {
    return this.clampValue(this.component.defaultValue);
  }

  // The position the controls should show: the stored answer when there is one,
  // otherwise the (unpersisted) default. Deliberately independent of
  // `touched`/isEmpty(), which carry required-validation semantics.
  get displayValue() {
    const value = this.dataValue;
    return value === undefined || value === null || value === ""
      ? this.emptyValue
      : this.clampValue(value);
  }

  clampValue(value) {
    return Math.min(this.max, Math.max(this.min, this.toNumber(value, this.min)));
  }

  get isRequired() {
    return !!this.component.validate?.required;
  }

  isEmpty(value = this.dataValue) {
    // An untouched slider always reports its numeric default, so a required
    // slider counts as empty until it is moved or given a real value.
    if (this.isRequired && !this.touched) {
      return true;
    }
    return value === undefined || value === null || value === "";
  }

  normalizeValue(value) {
    return value === undefined || value === null || value === ""
      ? this.emptyValue
      : this.clampValue(value);
  }

  // Push a value to the visible controls without a full redraw.
  syncDisplay(value) {
    const input = this.refs.input?.[0];
    if (input) {
      input.value = value;
      // Keep the attribute in sync with the property: the thumb position of a
      // freshly parsed or cloned input comes from the attribute.
      input.setAttribute("value", String(value));
      input.setAttribute("aria-valuenow", String(value));
    }
    if (this.refs.sliderValue) {
      this.refs.sliderValue.textContent = String(value);
    }
  }

  render() {
    const value = this.displayValue;
    const showValue = this.component.showValue !== false;
    // Reserve space for the widest value the slider can show so the track
    // width stays constant as the number grows (e.g. 10 -> 100 -> 1000).
    const reserve = Math.max(String(this.min).length, String(this.max).length);

    return super.render(`
      <div class="slider-wrapper" ref="wrapper">
        <input
          type="range"
          ref="input"
          class="slider-input"
          min="${this.min}"
          max="${this.max}"
          step="${this.step}"
          value="${value}"
          ${this.options.readOnly ? "disabled" : ""}
          aria-label="${this.t(this.component.label || "Slider")}"
          aria-valuemin="${this.min}"
          aria-valuemax="${this.max}"
          aria-valuenow="${value}"
        />
        ${
          showValue
            ? `<span class="slider-value" ref="sliderValue" style="min-width: ${reserve}ch">${value}</span>`
            : ""
        }
      </div>
    `);
  }

  attach(element) {
    this.loadRefs(element, {
      wrapper: "single",
      input: "multiple",
      sliderValue: "single",
    });

    // `input` fires on every thumb movement, so it covers `change` too.
    const input = this.refs.input?.[0];
    if (input) {
      this.addEventListener(input, "input", () => this.updateFromInput());
    }

    // The value can arrive while we are detached - Formio applies the submission
    // independently of attach, and setValue() then has no refs to write to. The
    // markup we are attaching to was rendered before that, so push the current
    // value back in instead of leaving the thumb at the rendered position.
    this.syncDisplay(this.displayValue);

    return super.attach(element);
  }

  updateFromInput() {
    const input = this.refs.input?.[0];
    if (!input) return;

    const value = this.clampValue(input.value);
    this.touched = true;
    this.syncDisplay(value);
    this.updateValue(value);
  }

  getValue() {
    const input = this.refs.input?.[0];
    return input ? this.clampValue(input.value) : this.dataValue;
  }

  setValue(value, flags = {}) {
    // `resetValue` is Formio silently seeding the default because no real
    // submission value exists yet - not a real answer. Anything else (a loaded
    // value or the user dragging the thumb) is.
    this.touched = flags.resetValue !== true;

    // Formio runs that seeding pass over every component whenever it is handed
    // data with no key for us - e.g. a submission built from a server payload
    // that predates the answer. An answer already in the data survives it: every
    // other component type keeps its value there, because their reset is a
    // no-op, and a genuine reset (Component.resetValue) unsets the key first.
    if (!this.touched && this.hasValue()) {
      this.touched = true;
      this.syncDisplay(this.displayValue);
      return false;
    }

    if (!this.touched) {
      // Show the default position but don't persist it: unsetting the key (via
      // dataValue = null) keeps the untouched default out of required-validation
      // and score calculation. Routing null through super.setValue would
      // normalizeValue() it straight back to the default.
      this.syncDisplay(this.emptyValue);
      this.dataValue = null;
      return false;
    }

    const normalized = this.normalizeValue(value);
    this.syncDisplay(normalized);
    return super.setValue(normalized, flags);
  }

  // Formio auto-seeds `component.defaultValue` into the submission data the
  // first time `dataValue` is read; suppress that while untouched so the
  // default never becomes a real, scored answer on its own.
  get shouldAddDefaultValue() {
    return this.touched && super.shouldAddDefaultValue;
  }
}

Formio.registerComponent("slider", CustomSliderComponent);

export default CustomSliderComponent;
